import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { VinylService } from '../services/vinylService';
import { Vinyl } from '../types';

export const AdminVinylsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [vinyls, setVinyls] = useState<Vinyl[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    color: '',
    type: '',
    basePrice: '',
    markupFactor: '2.0',
    width: '',
    isActive: true,
    order: '0',
  });

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await VinylService.getAllVinyls();
      setVinyls(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as any;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError(null);

      const vinylData = {
        name: formData.name,
        color: formData.color,
        type: formData.type,
        basePrice: parseFloat(formData.basePrice) || 0,
        markupFactor: parseFloat(formData.markupFactor) || 2.0,
        width: parseFloat(formData.width) || 0,
        isActive: formData.isActive,
        order: parseInt(formData.order) || 0,
      };

      if (editingId) {
        await VinylService.updateVinyl(editingId, vinylData as any);
      } else {
        await VinylService.createVinyl(vinylData as any);
      }

      await loadData();
      resetForm();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleEdit = (vinyl: Vinyl) => {
    setEditingId(vinyl.id);
    setFormData({
      name: vinyl.name,
      color: vinyl.color,
      type: vinyl.type,
      basePrice: vinyl.basePrice.toString(),
      markupFactor: vinyl.markupFactor.toString(),
      width: vinyl.width.toString(),
      isActive: vinyl.isActive,
      order: vinyl.order.toString(),
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this vinyl?')) return;
    try {
      await VinylService.deleteVinyl(id);
      await loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      name: '',
      color: '',
      type: '',
      basePrice: '',
      markupFactor: '2.0',
      width: '',
      isActive: true,
      order: '0',
    });
    setShowForm(false);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <p>Loading vinyls...</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1>Vinyl Types</h1>
        <button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          style={{
            padding: '10px 16px',
            background: 'linear-gradient(135deg, var(--header-background) 0%, var(--header-button) 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
          }}
        >
          + Add Vinyl
        </button>
      </div>

      {error && (
        <div style={{ padding: '12px 16px', background: '#fee2e2', color: '#c33', marginBottom: '20px', borderRadius: '6px' }}>
          {error}
        </div>
      )}

      {showForm && (
        <div style={{ background: 'white', padding: '20px', marginBottom: '24px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
              <div>
                <label>Name*</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="e.g., White Vinyl" required style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }} />
              </div>

              <div>
                <label>Color*</label>
                <input type="text" name="color" value={formData.color} onChange={handleChange} placeholder="e.g., White" required style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }} />
              </div>

              <div>
                <label>Type*</label>
                <input type="text" name="type" value={formData.type} onChange={handleChange} placeholder="e.g., Matte" required style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }} />
              </div>

              <div>
                <label>Price Per Sqft ($)*</label>
                <input type="number" name="basePrice" value={formData.basePrice} onChange={handleChange} step="0.01" required style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }} />
              </div>

              <div>
                <label>Markup Factor</label>
                <input type="number" name="markupFactor" value={formData.markupFactor} onChange={handleChange} min="1" max="5" step="0.1" style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }} />
              </div>

              <div>
                <label>Roll Width (inches)</label>
                <input type="number" name="width" value={formData.width} onChange={handleChange} step="1" style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="submit" style={{ padding: '10px 16px', background: 'var(--primary-button)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
                {editingId ? 'Update' : 'Create'} Vinyl
              </button>
              <button type="button" onClick={resetForm} style={{ padding: '10px 16px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div style={{ background: 'white', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
            <tr>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Name</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Color</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Type</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Price/Sqft</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Markup</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {vinyls.map((vinyl) => (
              <tr key={vinyl.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '12px 16px' }}>{vinyl.name}</td>
                <td style={{ padding: '12px 16px' }}>{vinyl.color}</td>
                <td style={{ padding: '12px 16px' }}>{vinyl.type}</td>
                <td style={{ padding: '12px 16px' }}>${vinyl.basePrice.toFixed(2)}</td>
                <td style={{ padding: '12px 16px' }}>{((vinyl.markupFactor - 1) * 100).toFixed(0)}%</td>
                <td style={{ padding: '12px 16px' }}>
                  <button onClick={() => handleEdit(vinyl)} style={{ padding: '6px 12px', marginRight: '8px', background: '#dbeafe', color: '#1e40af', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>
                    Edit
                  </button>
                  <button onClick={() => handleDelete(vinyl.id)} style={{ padding: '6px 12px', background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
